import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { CircleCheck } from "@dreamhub/designsystem/icons";
import OptionsDropdownMenu from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import NotFoundPage from "@/components/layouts/NotFoundPage";
import { Button } from "@/components/ui/button";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import ToastBody from "@/components/ui/toast-body";
import useAccount from "@/entities/accounts/hooks";
import {
  useDisconnectService,
  useSettingsTokens
} from "@/entities/settings/hooks";
import { useUsers, useUsersMe } from "@/entities/users/hooks";
import { ReservedUserIds } from "@/entities/users/types";
import { AuthRedirectProps } from "@/services/auth/AuthRedirect";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { ApolloSettings } from "@/pages/settings/components/integrations/apollo/ApolloSettings";
import { StripeCheckoutSettings } from "@/pages/settings/components/integrations/stripeCheckout/StripeCheckoutSettings";
import { WebFormSettings } from "@/pages/settings/components/integrations/webForm/WebFormSettings";
import { HubSpotSettings } from "@/pages/settings/components/integrations/hubspot/HubSpotSettings";
import IntegrationsWrapper from "@/pages/Integrations/components/IntegrationsWrapper";
import IntegrationLogo from "@/pages/settings/components/integrations/IntegrationLogo";
import IntegrationsTopBar from "@/pages/settings/components/integrations/IntegrationsTopBar";
import { ApiKeyConnectModal } from "@/pages/settings/components/integrations/ApiKeyConnectModal";
import ZoomPhoneSetting from "@/pages/settings/components/integrations/ZoomPhoneSetting";
import Banner from "@/components/ui/banner";
import {
  ESIGN_CONNECTION_QUERY,
  EsignConnectionState,
  EsignConnectionStatus,
  useEsignConnection
} from "@/pages/settings/components/integrations/pandadoc/esignConnection";
import { PandaDocReps } from "@/pages/settings/components/integrations/pandadoc/PandaDocReps";
import {
  AmpersandIntegration,
  INTEGRATIONS_TRANSLATION_PREFIX,
  SETUP_TOAST_DURATION,
  useIntegrationById
} from "@/pages/settings/components/integrations/integrations";
import {
  canDisconnectService,
  getServiceConnection
} from "@/pages/settings/components/connectedAccounts/serviceConnection";
import { useCanAccessAdminSettings } from "@/pages/settings/hooks";

const CONNECTED_ACCOUNTS_PREFIX = "connectedAccounts";

const InstalledBy: React.FC<{ userId: string }> = ({ userId }) => {
  const { t } = useTranslation();
  const installer = useUsers([userId]).users[0];

  if (
    !installer?.displayName ||
    installer.id === ReservedUserIds.UNKNOWN_USER_ID
  ) {
    return null;
  }

  return (
    <Text variant="bodySecondary" data-test="integration-installed-by">
      {t(`${INTEGRATIONS_TRANSLATION_PREFIX}.installedBy`, {
        name: installer.displayName
      })}
    </Text>
  );
};

const AmpersandSetup: React.FC<{
  integration: AmpersandIntegration;
  name: string;
}> = ({ integration, name }) => {
  const { t } = useTranslation();
  const account = useAccount().data;
  const userData = useUsersMe().data;

  if (!account || !userData) {
    return null;
  }

  const showSuccess = (key: "connected" | "updated" | "disconnected") =>
    toast.success(
      <ToastBody
        title={t(`${INTEGRATIONS_TRANSLATION_PREFIX}.setup.${key}`, { name })}
      />,
      { duration: SETUP_TOAST_DURATION }
    );

  return (
    <IntegrationsWrapper
      userData={userData}
      account={account}
      integrationName={integration.id}
      onInstallSuccess={() => showSuccess("connected")}
      onUpdateSuccess={() => showSuccess("updated")}
      onUninstallSuccess={() => showSuccess("disconnected")}
      className="min-h-0 w-full"
    />
  );
};

interface IntegrationDetailsProps {
  onAuthRedirect: (props: AuthRedirectProps) => void;
}

export const IntegrationDetails: React.FC<IntegrationDetailsProps> = ({
  onAuthRedirect
}) => {
  const { t } = useTranslation();
  const isSettingsAdmin = useCanAccessAdminSettings();
  const queryClient = useQueryClient();
  const { integrationId } = useParams();
  const {
    data: tokensData,
    isLoading,
    isError: tokensError
  } = useSettingsTokens();
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [isKeyModalOpen, setIsKeyModalOpen] = useState(false);
  const [isReplacingKey, setIsReplacingKey] = useState(false);

  const { integration, loading: isLoadingIntegration } =
    useIntegrationById(integrationId);
  const { disconnect: disconnectService } = useDisconnectService(
    integration && "serviceId" in integration ? integration.serviceId : ""
  );
  const { enabled: showApolloSettings } = useFeatureFlag(
    FeatureFlags.CRM_10405_APOLLO_SETTINGS
  );
  // PandaDoc's own status: whether its key needs replacing, and whom it matched.
  const isPandaDoc = integrationId === "pandadoc";
  const esignStatus = useEsignConnection(isPandaDoc) as {
    data: EsignConnectionStatus | undefined;
    isLoading: boolean;
    isError: boolean;
  };
  // A disabled query still hands back what it cached, so only PandaDoc's own
  // page reads it.
  const esign = isPandaDoc ? esignStatus.data : undefined;
  const esignLoading = isPandaDoc && esignStatus.isLoading;

  if (!integration && isLoadingIntegration) {
    return null;
  }

  if (!integration) {
    return <NotFoundPage data-test="integration-details-404" />;
  }

  const Icon = integration.icon;
  const itemPrefix = `${INTEGRATIONS_TRANSLATION_PREFIX}.items.${integration.id}`;
  const name = t(`${itemPrefix}.name`);

  const renderHeader = () => (
    <div className="flex w-full items-center gap-4">
      <IntegrationLogo icon={<Icon />} size="lg" />
      <div className="flex flex-col">
        <Text variant="title3H1">{name}</Text>
        <Text variant="bodySecondary">{t(`${itemPrefix}.description`)}</Text>
      </div>
    </div>
  );

  if (integration.kind === "native") {
    return (
      <div
        className="flex w-full flex-col"
        data-test="settings-integration-details-page"
      >
        <IntegrationsTopBar current={name} />
        <div className="mb-4 flex w-full items-center justify-center">
          <div className="flex w-[800px] flex-col items-start gap-12 pt-12">
            {renderHeader()}
            {integration.id === "webForm" && <WebFormSettings />}
            {integration.id === "stripeCheckout" && <StripeCheckoutSettings />}
          </div>
        </div>
      </div>
    );
  }

  if (integration.kind === "ampersand") {
    return (
      <div
        className="flex w-full flex-col"
        data-test="settings-integration-details-page"
      >
        <IntegrationsTopBar current={name} />
        <div className="mb-4 flex w-full items-center justify-center">
          <div className="flex w-[800px] flex-col items-start gap-12 pt-12">
            {renderHeader()}
            <AmpersandSetup integration={integration} name={name} />
            {integration.id === "apollo" && showApolloSettings && (
              <ApolloSettings />
            )}
            {/* The HubSpot entry itself is behind its settings flag. */}
            {integration.id === "hubspot" && <HubSpotSettings />}
          </div>
        </div>
      </div>
    );
  }

  const { allTokensValid: isConnected, tokenUser } = getServiceConnection(
    tokensData?.tokens,
    integration.serviceId
  );
  // A failed read keeps a stale status; PandaDoc's own counts once connected.
  const statusUnknown =
    tokensError || (isPandaDoc && isConnected && esignStatus.isError);
  const canDisconnect = canDisconnectService(
    integration.serviceId,
    isSettingsAdmin
  );
  const canReplaceKey = integration.kind === "apiKey";
  const disconnectTooltip = canDisconnect
    ? undefined
    : t(`${CONNECTED_ACCOUNTS_PREFIX}.disconnectTooltip`, { service: name });

  const handleConnect = () => {
    if (integration.kind === "apiKey") {
      setIsReplacingKey(false);
      setIsKeyModalOpen(true);
      return;
    }
    onAuthRedirect({
      to: integration.registerTo,
      method: "POST",
      redirectTo: `/settings/integrations/${integration.id}`
    });
  };

  const handleConfirmDisconnect = async () => {
    setIsDisconnectModalOpen(false);
    try {
      await disconnectService();
    } catch (error) {
      console.error(`${name} disconnection error:`, error);
      toast.error(t(`${CONNECTED_ACCOUNTS_PREFIX}.disconnectError`, { name }));
    }
    // A failed disconnect may still have removed the key, so re-read it.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["settings/tokens/all"] }),
      queryClient.invalidateQueries({ queryKey: [ESIGN_CONNECTION_QUERY] })
    ]).catch(() => {});
  };

  const renderStatus = () => {
    // PandaDoc's status waits for esignconnector too, so a key that stopped
    // working never shows as not connected first.
    if (isLoading || esignLoading) {
      return (
        <Text variant="bodySecondary" data-test="integration-status-loading">
          {t(`${CONNECTED_ACCOUNTS_PREFIX}.checking`)}
        </Text>
      );
    }

    if (statusUnknown) {
      return (
        <div
          className="flex w-full items-center rounded-2xl border-[0.5px] border-border-widget-card bg-surface-widget-card p-4"
          data-test="integration-status-error"
        >
          <Text variant="smBodyError">
            {t(`${INTEGRATIONS_TRANSLATION_PREFIX}.statusError`)}
          </Text>
        </div>
      );
    }

    if (esign?.state === EsignConnectionState.NEEDS_RECONNECT) {
      return (
        <div
          className="flex w-full flex-col gap-2"
          data-test="integration-status-needs-reconnect"
        >
          <Banner
            variant="warning"
            title={t(`${itemPrefix}.needsReconnect.title`)}
            buttonText={t(`${itemPrefix}.needsReconnect.action`)}
            onButtonClick={handleConnect}
            showCloseButton={false}
          />
          <Text variant="bodySecondary" className="px-1">
            {t(`${itemPrefix}.needsReconnect.body`)}
          </Text>
        </div>
      );
    }

    if (!isConnected) {
      return (
        <div
          className="flex w-full items-center justify-between rounded-2xl border-[0.5px] border-border-widget-card bg-surface-widget-card p-4"
          data-test="integration-status-disconnected"
        >
          <Text variant="bodySecondary">
            {t(`${INTEGRATIONS_TRANSLATION_PREFIX}.notConnected`, { name })}
          </Text>
          <Button
            variant="secondary"
            onClick={handleConnect}
            data-test="integration-details-connect"
          >
            {t(`${INTEGRATIONS_TRANSLATION_PREFIX}.connect`)}
          </Button>
        </div>
      );
    }

    return (
      <div
        className="flex w-full flex-col gap-2 rounded-2xl bg-badges-positive pt-2"
        data-test="integration-status-connected"
      >
        <div className="flex items-center justify-between px-4">
          <div className="flex items-center gap-1">
            <IconContainer size="sm" color="positive">
              <CircleCheck />
            </IconContainer>
            <Text variant="body" className="text-positive">
              {t(`${INTEGRATIONS_TRANSLATION_PREFIX}.connected`)}
            </Text>
          </div>
          <OptionsDropdownMenu
            options={{
              ...(canReplaceKey
                ? {
                    replaceKey: {
                      action: () => {
                        setIsReplacingKey(true);
                        setIsKeyModalOpen(true);
                      }
                    }
                  }
                : {}),
              disconnect: {
                action: () => setIsDisconnectModalOpen(true),
                disabled: !canDisconnect,
                disabledTooltipText: disconnectTooltip
              }
            }}
            tPrefix={`${CONNECTED_ACCOUNTS_PREFIX}.menu`}
            triggerIcon="horizontal-ellipsis"
            triggerDataTest="integration-details-menu"
            buttonVariant="iconNoBg"
            iconSize="lg"
            iconColor="secondary"
            disabled={!canDisconnect && !canReplaceKey}
            tooltipText={
              canDisconnect || canReplaceKey ? undefined : disconnectTooltip
            }
          />
        </div>
        <div className="flex flex-col rounded-2xl border-[0.5px] border-border-widget-card bg-surface-widget-card p-4">
          <Text variant="body">
            {t(`${INTEGRATIONS_TRANSLATION_PREFIX}.plugin`, { name })}
          </Text>
          {tokenUser && <InstalledBy userId={tokenUser} />}
        </div>
      </div>
    );
  };

  return (
    <div
      className="flex w-full flex-col"
      data-test="settings-integration-details-page"
    >
      <IntegrationsTopBar current={name} />
      <div className="mb-4 flex w-full items-center justify-center">
        <div className="flex w-[800px] flex-col items-start gap-12 pt-12">
          {renderHeader()}
          {renderStatus()}
          {integration.id === "zoom" &&
            !isLoading &&
            !statusUnknown &&
            isConnected && <ZoomPhoneSetting />}
          {/* A failed refetch keeps the last status, so the tokens must agree too. */}
          {!isLoading &&
            !statusUnknown &&
            isConnected &&
            esign?.state === EsignConnectionState.CONNECTED && (
              <PandaDocReps
                matchedUserIds={esign.matchedUserIds}
                unmatchedUserIds={esign.unmatchedUserIds}
              />
            )}
        </div>
      </div>
      <ConfirmationModal
        isOpen={isDisconnectModalOpen}
        setIsOpen={setIsDisconnectModalOpen}
        title={t(`${CONNECTED_ACCOUNTS_PREFIX}.disconnectModal.title`, {
          name
        })}
        confirmationText=""
        handleSubmit={handleConfirmDisconnect}
        submitButtonText={t(`${CONNECTED_ACCOUNTS_PREFIX}.menu.disconnect`)}
        hideDivider
        headerClassname="border-none"
      />
      {integration.kind === "apiKey" && (
        <ApiKeyConnectModal
          integration={integration}
          name={name}
          isOpen={isKeyModalOpen}
          onClose={() => setIsKeyModalOpen(false)}
          replacing={isReplacingKey}
        />
      )}
    </div>
  );
};
